import React, { Fragment, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import MUIDataTable from 'mui-datatables';
import parse from 'html-react-parser';
import { firstBy } from 'thenby';

import { ThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';

import DownloadIcon from '@material-ui/icons/GetApp';
import FilterListIcon from '@material-ui/icons/FilterList';

import pushToGTM, { downloadTableCsvTagGA4 } from '../../utils/analytics/gtm';
import { URL } from '../../utils/constants/general';
import { COLORS, SPACING } from '../../styles/constants';
import { getUniquePropertyValues } from '../../utils/helperFunctions';
import { CIDS, COLUMN_DISPLAY, ROWS_PER_PAGE, SORT_DIRECTIONS } from './constants';
import { updateRowsPerPage, updateSortOrder } from './productsTableSlice';
import {
	getNumberOfProducts,
	getNumberOfProductGroups,
	selectMDTProcessedColumns
} from '../../redux/slices/categoryDataSlice';
import { useFilters } from '../../hooks/useFilters';
import tableTheme from './theme';
import appOrientedTableTheme from './appOrientedTheme';
import { useStyles } from './styles';
import useProductsTableScroll from './useProductsTableScroll';
import TableViewCol from './TableViewCol';
import DisplayModeAction from './DisplayModeAction';
import { cleanHTMLtags } from './utils';
import WdTitle from './WdTitle/index';
import { superSearch } from '@codewell/super-search';
import { getCurrentLanguage } from '../../utils/i18n';

const ProductsTable = ({ categoryId, filtersOpen, handleFiltersOpen }) => {
	const classes = useStyles({ filtersOpen });
	const { t } = useTranslation(['main', 'category']);
	const containerRef = useRef(null);
	const [displayModeIsWPN, setDisplayModeIsWPN] = useState(false);
	const MDTProcessedColumns = useSelector(state => selectMDTProcessedColumns(state, displayModeIsWPN));
	const columns = useSelector(state => state.categoryData.columns);
	const filteredProducts = useSelector(state => state.categoryData.filteredProducts);
	const numberOfProducts = useSelector(getNumberOfProducts);
	const numberOfProductGroups = useSelector(getNumberOfProductGroups);
	const rowsPerPage = useSelector(state => state.productsTable.rowsPerPage);
	const sortOrder = useSelector(state => state.productsTable.sortOrder);
	const applicationOriented = useSelector(state => state.categoryData.applicationOriented);
	const dispatch = useDispatch();
	const tableScrollRef = useRef(null);
	const scrollBarRef = useRef(null);
	const scrollThumbRef = useRef(null);
	const lang = getCurrentLanguage();

	useFilters(categoryId, displayModeIsWPN);
	const updateThumb = useProductsTableScroll(tableScrollRef, scrollBarRef, scrollThumbRef);

	const changeDisplayModeIsWPN = newDisplayModeIsWPN => {
		if (newDisplayModeIsWPN)
			dispatch(
				updateSortOrder({
					name: CIDS.RANK,
					direction: SORT_DIRECTIONS.ASC
				})
			);

		setDisplayModeIsWPN(newDisplayModeIsWPN);
	};

	const scrollToTitle = () =>
		window.scrollTo({
			top: containerRef.current.offsetTop - tableTheme.spacing(SPACING),
			left: 0,
			behavior: 'smooth'
		});

	const sortEmptyValuesOnBottom = (data, colIndex, order) => {
		return data.sort((aData, bData) => {
			const a = aData.data[colIndex];
			const b = bData.data[colIndex];

			if (a === b) {
				const rankIndex = columns.findIndex(column => column.cid === CIDS.RANK);
				const rankA = aData.data[rankIndex];
				const rankB = bData.data[rankIndex];
				return rankA - rankB;
			} else if (a === null || a === '-') {
				return 1;
			} else if (b === null || b === '-') {
				return -1;
			}
			if (order === 'desc') {
				if (isNumeric(a) && isNumeric(b)) {
					return b - a;
				}
				return ('' + b).localeCompare(a);
			} else {
				if (isNumeric(a) && isNumeric(b)) {
					return a - b;
				}
				return ('' + a).localeCompare(b);
			}
		});
	};

	function isNumeric(str) {
		if (typeof str == 'number') return true;
		if (typeof str != 'string') return false; // we only process strings!
		return (
			!isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
			!isNaN(parseFloat(str))
		); // ...and ensure strings of whitespace fail
	}

	const sortWPNValuesByRanking = data => {
		try {
			const rankWPNIndex = columns.findIndex(column => column.cid === CIDS.RANK_WPN);

			return data.sort(
				firstBy((a, b) => a.data[rankWPNIndex] - b.data[rankWPNIndex], {
					direction: SORT_DIRECTIONS.ASC
				})
			);
		} catch (e) {
			console.error('Error sorting in WPN display mode:', e);
			return data;
		}
	};

	const getTableHeaderTooltip = (colWithDesc, column) => {
		if (lang === 'cn') {
			if (colWithDesc && i18next.exists(`filtersDesc.${colWithDesc.cid}`)) {
				if (colWithDesc.cid === 'vbr') {
					return categoryId === 'powerIntegratedModule'
						? t(`filtersDesc.vbrPim`)
						: categoryId === 'digitalBjt'
						? t(`filtersDesc.vbrDigBJT`)
						: t(`filtersDesc.vbr`);
				} else if (colWithDesc.cid === 'pd' && (categoryId === 'analogBjt' || categoryId === 'rfBjt')) {
					return t('filtersDesc.pdBJT');
				} else if (colWithDesc.cid === 'tpl' && categoryId === 'gateDriver') {
					return t('filtersDesc.tplGD');
				} else if (colWithDesc.cid === 'irx' && categoryId === 'smallSignalDiode') {
					return t('filtersDesc.irxSSDiode');
				} else if (colWithDesc.cid === 'vgsm' && categoryId === 'jfet') {
					return t('filtersDesc.vgsmJFET');
				} else if (colWithDesc.cid === 'chp' && categoryId === 'mosfet') {
					return t('filtersDesc.chpMosfet');
				} else if (colWithDesc.cid === 'vccn' && categoryId === 'comparator') {
					return t('filtersDesc.vccnComparator');
				} else if (colWithDesc.cid === 'vccx' && categoryId === 'comparator') {
					return t('filtersDesc.vccxComparator');
				} else if (colWithDesc.cid === 'vcm' && categoryId === 'rfBjt') {
					return t('filtersDesc.vcmRFBJT');
				} else if (colWithDesc.cid === 'idmx' && categoryId === 'jfet') {
					return t('filtersDesc.idmxJFET');
				} else {
					return t(`filtersDesc.${colWithDesc.cid}`);
				}
			} else if (i18next.exists(`filters.${colWithDesc.cid}`)) {
				return cleanHTMLtags(parse(t(`filters.${colWithDesc.cid}`)));
			}
		}

		return colWithDesc && colWithDesc.description
			? cleanHTMLtags(colWithDesc.description)
			: cleanHTMLtags(parse(column.label));
	};

	return (
		<div id="Products_Table__container" ref={containerRef}>
			<ThemeProvider theme={applicationOriented ? appOrientedTableTheme : tableTheme}>
				{applicationOriented && <WdTitle />}
				<div className={classes.scrollBarContainer}>
					<div ref={scrollBarRef} className={classes.scrollBarTrack}>
						<div ref={scrollThumbRef} className={classes.scrollBarThumb} />
					</div>
				</div>
				<div ref={tableScrollRef}>
					<MUIDataTable
						title={
							<Fragment>
								{!filtersOpen && (
									<Tooltip title={t('main:components.productsTable.buttons.openFilters')} aria-label="open-filters">
										<IconButton onClick={() => handleFiltersOpen(true)} className={classes.filtersOpenButton}>
											<FilterListIcon />
										</IconButton>
									</Tooltip>
								)}

								<div className={classes.titleText}>
									<Typography id="Products_Table__number_of_filtered_products" variant="subtitle2" display="inline">
										{displayModeIsWPN
											? getUniquePropertyValues(filteredProducts, CIDS.GENERAL_PART_NUMBER).length
											: filteredProducts.length}
									</Typography>

									<Typography variant="subtitle2" color="textSecondary" display="inline">
										{t('main:components.productsTable.displayMode.of')}
										<span id="Products_Table__number_of_total_products">
											{displayModeIsWPN ? numberOfProductGroups : numberOfProducts}
										</span>
										{displayModeIsWPN
											? t('main:components.productsTable.displayMode.WPN.endOfTitle')
											: t('main:components.productsTable.displayMode.OPN.endOfTitle')}
									</Typography>
								</div>
							</Fragment>
						}
						columns={MDTProcessedColumns}
						data={filteredProducts}
						components={{
							TableViewCol: ({ columns }) => (
								<TableViewCol
									viewableColumns={columns.filter(col => col.display !== COLUMN_DISPLAY.EXCLUDED)}
									adjustToolbarWidth={() => {
										const tableEl = tableScrollRef.current?.querySelector("table[role='grid']")?.parentElement;
										updateThumb(tableEl);
									}}
								/>
							),
							icons: {
								DownloadIcon
							}
						}}
						options={{
							// customSearchRender nicer input component
							customSearch: (searchQuery, currentRow, columns) => {
								const superSearchResult = superSearch({ caseSensitive: false })(searchQuery.toLowerCase(), {
									...currentRow
								});
								return superSearchResult.numberOfMatches > 0 && superSearchResult.fullMatch;
							},
							customSort: displayModeIsWPN ? sortWPNValuesByRanking : sortEmptyValuesOnBottom,
							customToolbar: () => (
								<Grid
									container
									alignItems="center"
									justifyContent="flex-end"
									spacing={numberOfProducts !== numberOfProductGroups ? 2 : 1}
									className={classes.additionalActionsContainer}>
									<Grid item>
										{numberOfProducts !== numberOfProductGroups && (
											<DisplayModeAction
												displayModeIsWPN={displayModeIsWPN}
												changeDisplayModeIsWPN={changeDisplayModeIsWPN}
											/>
										)}
									</Grid>
								</Grid>
							),
							downloadOptions: {
								filename:
									'PRT+ ' +
									t('category:' + categoryId + '.title') +
									' ' +
									t('main:components.productsTable.downloadOption.filenameEnding') +
									'.csv'
							},
							draggableColumns: { enabled: true },
							elevation: 1,
							filter: false,
							fixedHeader: false, // test other browsers
							jumpToPage: true,
							onChangePage: () => scrollToTitle(),
							onChangeRowsPerPage: numberOfRows => dispatch(updateRowsPerPage(numberOfRows)),
							onColumnSortChange: (changedColumn, direction) => {
								dispatch(
									updateSortOrder({
										name: changedColumn,
										direction: direction
									})
								);

								if (displayModeIsWPN) changeDisplayModeIsWPN(false);
							},
							onDownload: (buildHead, buildBody, columns, data) => {
								pushToGTM(downloadTableCsvTagGA4());

								/* convert datasheet property to link */
								try {
									columns.forEach(column => {
										if (column.name === CIDS.PART_NUMBER) {
											column.download = true;
										}
									});
									const indexOfDatasheetColumn = columns.findIndex(column => column.name === CIDS.DATASHEET);

									const formattedData = data.map(product => ({
										...product,
										data: product.data.map((productData, index) =>
											index === indexOfDatasheetColumn ? URL.DATASHEET + productData : productData
										)
									}));

									return '\uFEFF' + buildHead(columns) + buildBody(formattedData);
								} catch (e) {
									console.error('Error downloading products table csv:', e);
									return false;
								}
							},
							print: false,
							rowHover: false,
							rowsPerPage: rowsPerPage,
							rowsPerPageOptions: ROWS_PER_PAGE,
							searchPlaceholder: t('main:components.productsTable.searchPlaceHolder'),
							selectableRows: 'none', // temporary
							setRowProps: displayModeIsWPN
								? (_row, dataIndex, _rowIndex) => {
										const WPNGroup = filteredProducts[dataIndex].WPNGroup;
										return {
											className: {},
											style: WPNGroup
												? {
														height: displayModeIsWPN && WPNGroup.productsTotal > 1 && 42,
														borderTop:
															WPNGroup.productId === 1 && WPNGroup.id !== 1
																? `1px solid ${COLORS.BORDER_TABLE_ROW_DARK}`
																: 0,
														borderBottom:
															WPNGroup.productId === WPNGroup.productsTotal
																? `1px solid ${COLORS.BORDER_TABLE_ROW_DARK}`
																: 0
												  }
												: {}
										};
								  }
								: null,
							sortOrder: sortOrder,
							textLabels: {
								body: {
									columnHeaderTooltip: column => {
										const colWithDesc = columns.find(col => col.cid === column.name);
										return getTableHeaderTooltip(colWithDesc, column);
									},
									noMatch: t('main:components.productsTable.text.noProducts')
								},
								pagination: {
									rowsPerPage: t('main:components.productsTable.text.rowsPerPage'),
									jumpToPage: t('main:components.productsTable.text.jumpToPage'),
									displayRows: t('main:components.productsTable.displayMode.of')
								},
								toolbar: {
									viewColumns: t('main:components.productsTable.text.viewColumns'),
									search: t('main:components.productsTable.text.search'),
									downloadCsv: t('main:components.productsTable.text.download')
								}
							}
						}}
					/>
				</div>
			</ThemeProvider>
		</div>
	);
};

ProductsTable.propTypes = {
	categoryId: PropTypes.string.isRequired,
	filtersOpen: PropTypes.bool.isRequired,
	handleFiltersOpen: PropTypes.func.isRequired,
	applicationOriented: PropTypes.bool
};

export default React.memo(ProductsTable);
